import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import { Scene } from '@/interfaces/editor'
import { useEditorContext } from '@/scenes/engine'
import { selectDisableEditorState, selectEditorState } from '@/store/slices/editor/selector'
import { ObjectType } from '@/scenes/engine/common/constants'
import { LayoutUtils } from '@/utils/LayoutUtils'
import { getSceneThumbnailSizes, SCENE_THUMBNAIL_MIN_HEIGHT, SCENE_THUMBNAIL_MAX_HEIGHT } from './sceneThumbnailUtils'
import { SceneItemConfig, ScenesExternalDataProps, ThumbnailItemData, scenesConfig, downloadConfig } from './sceneItemConfig'

// ---------------------------------------------------------------------------
// Layout constants (consumed by Scenes.tsx shell and provider effects)
// ---------------------------------------------------------------------------

const HOVERING_CONTAINER_WIDTH_DIFF = 0
export const BASE_CONTAINER_WIDTH = LayoutUtils.PAGES_WIDTH
export const HOVERING_CONTAINER_WIDTH = BASE_CONTAINER_WIDTH + HOVERING_CONTAINER_WIDTH_DIFF
export const WRAP_SCENE_LIST_PADDING_WHEN_HOVERING =
  HOVERING_CONTAINER_WIDTH_DIFF === 0 ? '12px 8px 12px 12px' : '14px 10px 14px 14px'

// ---------------------------------------------------------------------------
// Sizing constants (re-exported for backward compat – ThumbnailContent uses them)
// ---------------------------------------------------------------------------

export const MINIMUM_SIZE_PREVIEW = SCENE_THUMBNAIL_MIN_HEIGHT
export const MAXIMUM_SIZE_PREVIEW = SCENE_THUMBNAIL_MAX_HEIGHT

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SceneSizesResult = {
  baseSize: { width: number; height: number }
  activeSize: { width: number; height: number }
  ratio: number
}

type DisableEditorState = ReturnType<typeof selectDisableEditorState>

interface ScenesContextType {
  // Item data (data-agnostic — editor scenes or external items)
  items: ThumbnailItemData[]
  selectedIds: string[]
  // Interaction state
  hoveringId: string | null
  isActive: boolean
  isContainerHovering: boolean
  openEditMenuId: string | null
  openDownloadPopoverSceneId: string | null
  isDragging: boolean
  // Setters
  setHoveringId: (id: string | null) => void
  setIsContainerHovering: (v: boolean) => void
  setOpenEditMenuId: (id: string | null) => void
  setOpenDownloadPopoverSceneId: React.Dispatch<React.SetStateAction<string | null>>
  setIsDragging: (v: boolean) => void
  // Config
  sceneItemConfig: SceneItemConfig
  showAddButton: boolean
  disableEditorState: DisableEditorState
  disable: boolean
  hide: boolean
  // Sizing
  baseSize: { width: number; height: number }
  activeSize: { width: number; height: number }
  defaultRatio: number
  // Delegated handlers
  handleAddScene: () => void
  handleSceneSelect: (item: ThumbnailItemData, event: React.MouseEvent, isTargetTriggerPopover: boolean) => void
  handleDragStart: (id: string) => void
  handleDragEnd: (activeId: string, overId: string, moveBackwards: boolean) => void
  handleDownloadBadgeClick: (item: ThumbnailItemData) => void
  handleDownloadPopoverOpenChange: (sceneId: string, isOpen: boolean) => void
  getDownloadDestination: () => string
  getDownloadSource: () => string
  getItemSizes: (item: ThumbnailItemData) => SceneSizesResult
  calculateRotation: (ratio: number) => number
  scrollSelectedIntoView: boolean
  onImageLoad?: (itemId: string, event: React.SyntheticEvent<HTMLImageElement>) => void
  onVideoLoad?: (itemId: string, event: React.SyntheticEvent<HTMLVideoElement>) => void
  // Refs
  wrapSceneListRef: React.RefObject<HTMLDivElement>
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ScenesContext = createContext<ScenesContextType | undefined>(undefined)

export const useScenesContext = (): ScenesContextType => {
  const ctx = useContext(ScenesContext)
  if (!ctx) {
    throw new Error('useScenesContext must be used within a ScenesProvider')
  }
  return ctx
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ScenesProviderProps {
  hide?: boolean
  disable?: boolean
  /** When provided, the provider uses external items/callbacks and skips all editor event listeners. */
  externalData?: ScenesExternalDataProps
  /** Editor-mode config override. Defaults to scenesConfig. */
  config?: SceneItemConfig
  /** Override the download destination label returned to SceneItem. Defaults to 'Desktop'. */
  getDownloadDestination: () => string
  /** Override the download source label returned to SceneItem. Defaults to 'Editor'. */
  getDownloadSource: () => string
  children: React.ReactNode
}

export const ScenesProvider: React.FC<ScenesProviderProps> = ({
  hide = false,
  disable = false,
  externalData,
  config: configProp,
  getDownloadDestination: getDownloadDestinationProp,
  getDownloadSource: getDownloadSourceProp,
  children,
}) => {
  const { editor, canvas } = useEditorContext()
  const disableEditorState = useSelector(selectDisableEditorState)
  const editorState = useSelector(selectEditorState)

  const isExternalMode = Boolean(externalData)

  // Editor-mode config: prefer prop override, otherwise use editorState to pick default
  const editorSceneItemConfig = configProp ?? (editorState.scenes === 'download' ? downloadConfig : scenesConfig)
  const sceneItemConfig = isExternalMode ? externalData!.config : editorSceneItemConfig
  const showAddButton = !isExternalMode && editorState.scenes === 'scenes'

  // --- Interaction state ---
  const [isActive, setIsActive] = useState(isExternalMode ? (externalData?.isActive ?? true) : false)
  const [isContainerHovering, setIsContainerHovering] = useState(false)
  const [openEditMenuId, setOpenEditMenuId] = useState<string | null>(null)
  const [openDownloadPopoverSceneId, setOpenDownloadPopoverSceneId] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  // --- Editor-mode scene state (unused in external mode) ---
  const [scenes, setScenes] = useState<Scene[]>([])
  const [selectedSceneIds, setSelectedSceneIds] = useState<string[]>([])
  const [hoveringId, setHoveringId] = useState<string | null>(null)

  const wrapSceneListRef = useRef<HTMLDivElement>(null)

  // --- Derived items/selectedIds exposed to context ---
  const items: ThumbnailItemData[] = isExternalMode
    ? externalData!.items
    : scenes.map((s) => ({
        id: s.sceneId,
        preview: s.preview,
        isLoading: s.state === 'loading',
        isDisabled: s.state === 'disabled',
      }))

  const selectedIds = isExternalMode ? externalData!.selectedIds : selectedSceneIds

  // Sync external openDownloadPopoverItemId into local state
  useEffect(() => {
    if (!isExternalMode) return
    setOpenDownloadPopoverSceneId(externalData!.openDownloadPopoverItemId ?? null)
  }, [isExternalMode, externalData?.openDownloadPopoverItemId])

  // Sync external isActive into local state
  useEffect(() => {
    if (!isExternalMode) return
    setIsActive(externalData?.isActive ?? true)
  }, [isExternalMode, externalData?.isActive])

  // --- Sizing ---

  const getItemSizes = useCallback(
    (item: ThumbnailItemData): SceneSizesResult => {
      if (isExternalMode) {
        const size = item.size ?? { width: 64, height: 64 }
        return { baseSize: size, activeSize: size, ratio: size.width / size.height }
      }
      const scene = scenes.find((s) => s.sceneId === item.id)
      const frame = editor.handlers.frameHandler.get()
      const sceneWidth = scene?.canvasSize?.width ?? frame?.width ?? 1080
      const sceneHeight = scene?.canvasSize?.height ?? frame?.height ?? 1080
      return getSceneThumbnailSizes({ width: sceneWidth, height: sceneHeight })
    },
    [isExternalMode, scenes, editor],
  )

  const { baseSize, activeSize, defaultRatio } = useMemo(() => {
    if (isExternalMode) return { baseSize: { width: 64, height: 64 }, activeSize: { width: 64, height: 64 }, defaultRatio: 1 }
    const frame = editor?.handlers.frameHandler.get()
    const sizes = getSceneThumbnailSizes({
      width: frame?.width ?? 1080,
      height: frame?.height ?? 1080,
    })
    return { baseSize: sizes.baseSize, activeSize: sizes.activeSize, defaultRatio: sizes.ratio }
  }, [isExternalMode, editor])

  const calculateRotation = useCallback((ratio: number): number => {
    if (Math.abs(ratio - 1) < 0.05) return 75
    if (ratio > 1) return 15 - (ratio - 1) * 2
    return -15 + (1 - ratio) * 2
  }, [])

  // --- Scene operations (editor mode only) ---

  const refreshScenes = useCallback(() => {
    setSelectedSceneIds(editor.handlers.pagesHandler.selectedSceneIds)
    setScenes([...editor.handlers.pagesHandler.scenes])
  }, [editor])

  const resetMultipageSelection = useCallback(() => {
    editor.handlers.pagesHandler.resetMultiPageSelection()
  }, [editor])

  const autoSelectFirstImageOnScene = useCallback(
    (sceneId: string) => {
      if (!sceneItemConfig.autoSelectActiveObjectWhenChangingScene) return
      const objects = editor.handlers.pagesHandler.getObjectsOnScene(sceneId) as any[]
      const targetObj = objects.filter((o) => o.type === ObjectType.BAZAART_IMAGE)?.[0]
      if (targetObj) {
        canvas.setActiveObject(targetObj)
        if (sceneItemConfig.focusActiveObjectWhenChangingScene) {
          editor.handlers.canvasFocusHandler.focus(
            [{ object: targetObj, config: { isInteractive: false, hasControls: false, hasBorders: false } }],
            () => {},
            'default',
          )
        }
        canvas.renderAll()
      }
    },
    [editor, canvas, sceneItemConfig],
  )

  const handleAddScene = useCallback(() => {
    if (isExternalMode) return
    canvas.discardActiveObject()
    editor.handlers.pagesHandler.addNewScene({ mode: 'append' })
    setTimeout(() => {
      wrapSceneListRef.current?.scrollTo({ top: wrapSceneListRef.current.scrollHeight, behavior: 'smooth' })
    }, 150)
  }, [isExternalMode, editor, canvas])

  // --- Handlers ---

  const handleSceneSelect = useCallback(
    (item: ThumbnailItemData, event: React.MouseEvent, isTargetTriggerPopover: boolean) => {
      if (isExternalMode) {
        externalData!.onSelect(item, event, isTargetTriggerPopover)
        return
      }
      const scene = scenes.find((s) => s.sceneId === item.id)
      if (!scene) return
      editor.handlers.pagesHandler.selectScene(
        scene,
        { ctrlKey: event.ctrlKey, metaKey: event.metaKey, shiftKey: event.shiftKey },
        isTargetTriggerPopover,
      )
      if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
        if (editor.handlers.pagesHandler.currentScene?.sceneId === item.id) {
          autoSelectFirstImageOnScene(item.id)
        }
      }
    },
    [isExternalMode, externalData, scenes, editor, autoSelectFirstImageOnScene],
  )

  const handleDragStart = useCallback(
    (id: string) => {
      if (isExternalMode) { externalData?.onDragStart?.(id); return }
      editor.handlers.pagesHandler.onDragStart(id)
    },
    [isExternalMode, externalData, editor],
  )

  const handleDragEnd = useCallback(
    (activeId: string, overId: string, moveBackwards: boolean) => {
      if (isExternalMode) { externalData?.onDragEnd?.(activeId, overId, moveBackwards); return }
      editor.handlers.pagesHandler.onDragEnd(activeId, overId, moveBackwards)
    },
    [isExternalMode, externalData, editor],
  )

  const handleDownloadBadgeClick = useCallback(
    (item: ThumbnailItemData) => {
      if (isExternalMode) { externalData?.onDownloadBadgeClick?.(item); return }
      setSelectedSceneIds((prev) => {
        if (prev.length === 1 && prev[0] === item.id) return prev
        return [item.id]
      })
      setOpenDownloadPopoverSceneId((prev) => (prev === item.id ? null : item.id))
    },
    [isExternalMode, externalData],
  )

  const handleDownloadPopoverOpenChange = useCallback(
    (sceneId: string, isOpen: boolean) => {
      if (isExternalMode) { externalData?.onDownloadPopoverOpenChange?.(sceneId, isOpen); return }
      setOpenDownloadPopoverSceneId((prev) => {
        if (isOpen) return sceneId
        return prev === sceneId ? null : prev
      })
    },
    [isExternalMode, externalData],
  )

  const getDownloadDestination = useCallback((): string => getDownloadDestinationProp?.() ?? 'Desktop', [getDownloadDestinationProp])
  const getDownloadSource = useCallback((): string => getDownloadSourceProp?.() ?? 'Editor', [getDownloadSourceProp])

  // --- Effects (editor mode only) ---

  useEffect(() => {
    if (isExternalMode || !editor) return
    ;(async () => { await editor.handlers.pagesHandler.updateScenesPreview() })()
  }, [isExternalMode, editor])

  useEffect(() => {
    if (isExternalMode || !editor) return
    const handleDocumentClick = (event: MouseEvent) => {
      if (wrapSceneListRef.current?.contains(event.target as Node)) {
        editor.handlers.pagesHandler.isActive = true
        setIsActive(true)
        return
      }
      editor.handlers.pagesHandler.isActive = false
      setIsActive(false)
      setIsContainerHovering(false)
    }
    const onActivePage = () => {
      setIsActive(true)
      editor.handlers.pagesHandler.isActive = true
    }
    document.addEventListener('click', handleDocumentClick)
    editor.on('page:active', onActivePage)
    return () => {
      document.removeEventListener('click', handleDocumentClick)
      editor?.off('page:active', onActivePage)
    }
  }, [isExternalMode, editor])

  useEffect(() => {
    if (isExternalMode || !wrapSceneListRef.current || isDragging) return
    if (hoveringId || openEditMenuId || openDownloadPopoverSceneId) {
      wrapSceneListRef.current.style.width = `${HOVERING_CONTAINER_WIDTH}px`
    } else {
      wrapSceneListRef.current.style.width = `${BASE_CONTAINER_WIDTH}px`
      setIsContainerHovering(false)
    }
  }, [isExternalMode, hoveringId, openEditMenuId, openDownloadPopoverSceneId, isDragging])

  useEffect(() => {
    if (isExternalMode || !editor || !canvas) return

    const updateScenePreview = async () => { await editor.handlers.pagesHandler.updateSingleScenePreview() }
    const updateAllScenesPreview = async () => { await editor.handlers.pagesHandler.updateScenesPreview() }
    const refreshAndUpdateScenes = () => refreshScenes()
    const handleScenePreviewChanged = () => refreshScenes()
    const handleCanvasClick = () => resetMultipageSelection()
    const handleAfterDelete = (scene: Scene) => {
      if (!scene) return
      autoSelectFirstImageOnScene(scene.sceneId)
    }

    editor.on('scenes:changed', refreshAndUpdateScenes)
    editor.on('scene-preview:changed', handleScenePreviewChanged)
    editor.on('scenes-preview:changed', handleScenePreviewChanged)
    editor.on('page-menu:opened', resetMultipageSelection)
    editor.on('background:changed', updateAllScenesPreview)
    editor.on('history:changed', updateScenePreview)
    editor.on('scene:after-delete', handleAfterDelete)
    canvas.on('mouse:down', handleCanvasClick)

    if (editor.handlers.pagesHandler.currentScene) {
      ;(async () => {
        await editor.handlers.pagesHandler.setCurrentScene(editor.handlers.pagesHandler.currentScene)
      })()
      refreshScenes()
      setSelectedSceneIds([editor.handlers.pagesHandler.currentScene.sceneId])
    }

    return () => {
      if (editor) {
        editor.off('scenes:changed', refreshAndUpdateScenes)
        editor.off('scene-preview:changed', handleScenePreviewChanged)
        editor.off('scenes-preview:changed', handleScenePreviewChanged)
        editor.off('page-menu:opened', resetMultipageSelection)
        editor.off('background:changed', updateAllScenesPreview)
        editor.off('history:changed', updateScenePreview)
        editor.off('scene:after-delete', handleAfterDelete)
        canvas.off('mouse:down', handleCanvasClick)
      }
    }
  }, [isExternalMode, editor, canvas, refreshScenes, resetMultipageSelection, autoSelectFirstImageOnScene])

  useEffect(() => {
    if (isExternalMode || !openDownloadPopoverSceneId) return
    const hasScene = scenes.some((s) => s.sceneId === openDownloadPopoverSceneId)
    if (!hasScene) setOpenDownloadPopoverSceneId(null)
  }, [isExternalMode, openDownloadPopoverSceneId, scenes])

  useEffect(() => {
    if (isExternalMode || !editor) return
    editor.handlers.pagesHandler.setSelectedSceneIds(selectedSceneIds)
  }, [isExternalMode, selectedSceneIds, editor])

  // ---------------------------------------------------------------------------

  const value: ScenesContextType = {
    items,
    selectedIds,
    hoveringId,
    isActive,
    isContainerHovering,
    openEditMenuId,
    openDownloadPopoverSceneId,
    isDragging,
    setHoveringId,
    setIsContainerHovering,
    setOpenEditMenuId,
    setOpenDownloadPopoverSceneId,
    setIsDragging,
    sceneItemConfig,
    showAddButton,
    disableEditorState,
    disable,
    hide,
    baseSize,
    activeSize,
    defaultRatio,
    handleAddScene,
    handleSceneSelect,
    handleDragStart,
    handleDragEnd,
    handleDownloadBadgeClick,
    handleDownloadPopoverOpenChange,
    getDownloadDestination,
    getDownloadSource,
    getItemSizes,
    calculateRotation,
    scrollSelectedIntoView: Boolean(isExternalMode && externalData?.scrollSelectedIntoView),
    onImageLoad: isExternalMode ? externalData?.onImageLoad : undefined,
    onVideoLoad: isExternalMode ? externalData?.onVideoLoad : undefined,
    wrapSceneListRef,
  }

  return <ScenesContext.Provider value={value}>{children}</ScenesContext.Provider>
}
